import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import gql from 'graphql-tag';
import { createStructuredSelector } from 'reselect';
import cloneDeep from 'lodash/cloneDeep';
import CommunityMessage from 'components/CommunityMessage';
import { GET_COMMUNITY_MESSAGES_QUERY } from 'utils/graphql/queries';
import fragments from 'utils/graphql/fragments';
import { errorShape } from 'utils/shapes';
import { fromIso9075DateString } from 'utils/functions';
import { updateCurrentMessage } from './actions';
import { selectCurrentId, selectPreviousId } from './selectors';

const defaultMessage = {
  id: -1,
  start: null,
  end: null,
  durationSec: 5,
  message: 'Play today and Find Your Winning Moment!'
};

const loadingMessage = {
  id: 0,
  start: null,
  end: null,
  durationSec: 1,
  message: ''
};

const maxDurationExtensionMs = 10000;
const minDurationMs = 1000;

class CommunityMessageContainer extends Component {
  constructor(props) {
    super(props);

    this.cycleMessage = this.cycleMessage.bind(this);
    this.unsubscribeFromUpdates = () => {};
  }

  componentDidMount() {
    this.unsubscribeFromUpdates = this.props.subscribeToUpdates();

    let startingMessage = this.getMessage(this.props.currentId);
    if (startingMessage === defaultMessage) {
      if (this.props.data.loading && !this.props.data.communityMessages) {
        startingMessage = loadingMessage;
      } else {
        startingMessage = this.findNextMessage(this.props.currentId);
      }
    }
    this.updateMessage(startingMessage);
  }

  componentWillUnmount() {
    this.unsubscribeFromUpdates();
    this.unsubscribeFromUpdates = () => {};

    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = undefined;
    }
  }

  cycleMessage() {
    let nextMessage = this.findNextMessage(this.props.currentId);
    this.updateMessage(nextMessage);
  }

  updateMessage(message) {
    this.props.updateCurrentMessage(message.id);

    // Adjust the duration for messages that are expiring soon
    let durationMs = message.durationSec * 1000;
    if (message.end) {
      const end = fromIso9075DateString(message.end);
      const timeToEndMs = end.getTime() - Date.now();
      if (
        timeToEndMs < durationMs ||
        timeToEndMs - durationMs < maxDurationExtensionMs
      ) {
        durationMs = timeToEndMs;
      }
    }
    if (!durationMs || durationMs < minDurationMs) {
      durationMs = minDurationMs;
    }

    this.timeout = setTimeout(this.cycleMessage, durationMs);
  }

  findNextMessage(currentId) {
    const data = this.props.data;
    const messages = data.communityMessages && data.communityMessages.messages;
    if (messages && messages.length) {
      const now = Date.now();
      let startIndex = messages.findIndex(msg => msg.id > currentId);
      if (startIndex === -1) {
        startIndex = 0;
      }
      let index = startIndex;
      do {
        const message = messages[index];
        if (
          (!message.start || now >= fromIso9075DateString(message.start)) &&
          (!message.end || now <= fromIso9075DateString(message.end))
        ) {
          return message;
        }
        ++index;
        if (index >= messages.length) {
          index = 0;
        }
      } while (index !== startIndex);
    }

    return defaultMessage;
  }

  getMessage(id) {
    if (id === loadingMessage.id) {
      return loadingMessage;
    }

    const data = this.props.data;
    const messages = data.communityMessages && data.communityMessages.messages;
    return (messages && messages.find(m => m.id === id)) || defaultMessage;
  }

  render() {
    const {
      currentId,
      previousId,
      data,
      updateCurrentMessage,
      ...otherProps
    } = this.props;

    return (
      <CommunityMessage
        previous={this.getMessage(previousId)}
        current={this.getMessage(currentId)}
        {...otherProps}
      />
    );
  }
}

CommunityMessageContainer.propTypes = {
  data: PropTypes.shape({
    communityMessages: PropTypes.shape({
      messages: PropTypes.arrayOf(PropTypes.object)
    }),
    loading: PropTypes.bool,
    error: errorShape
  }),
  previousId: PropTypes.number,
  currentId: PropTypes.number,
  updateCurrentMessage: PropTypes.func
};

const SUBSCRIPTION_COMMUNITY_MESSAGE_UPDATES = gql`
  subscription MyCommunityMessageUpdates {
    communityMessageUpdates {
      messages {
        ...CommunityMessageFields
      }
    }
  }
  ${fragments.communityMessage}
`;

const withData = graphql(GET_COMMUNITY_MESSAGES_QUERY, {
  options: props => ({
    variables: { input: { includeExpired: false } },
    fetchPolicy: 'cache-and-network'
  }),
  props: ({ ownProps, data }) => ({
    data,
    subscribeToUpdates: () => {
      return data.subscribeToMore({
        document: SUBSCRIPTION_COMMUNITY_MESSAGE_UPDATES,
        updateQuery: (prev, { subscriptionData: { data: cmUpdate } }) => {
          if (!cmUpdate) {
            return prev;
          }
          let result = cloneDeep(prev); // Do not modify prev
          result.communityMessages.messages = cmUpdate.messages;
          return result;
        }
      });
    }
  })
})(CommunityMessageContainer);

const mapStateToProps = createStructuredSelector({
  previousId: selectPreviousId(),
  currentId: selectCurrentId()
});

const mapDispatchToProps = dispatch => {
  return {
    updateCurrentMessage: id => {
      dispatch(updateCurrentMessage(id));
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withData);



// WEBPACK FOOTER //
// ./src/containers/CommunityMessageContainer/index.js